import { selectCurrency } from '@/redux/config/configSlice';
import { selectActiveTicket } from '@/redux/data/dataSlice';
import { ItemType } from '@/redux/data/dataTypes';
import { useAppSelector } from '@/redux/hooks';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';

interface ItemCardProps {
  item: ItemType;
  onItemPress: (item: ItemType) => void;
  size?: 'small' | 'medium' | 'large';
  className?: string;
}

const ItemCard: FC<ItemCardProps> = ({ item, onItemPress, size = 'medium', className = '' }) => {
  const { t, i18n } = useTranslation();
  const currency = useAppSelector(selectCurrency);
  const activeTicket = useAppSelector(selectActiveTicket);
  const lang = i18n.language;
  const isArabic = lang === 'ar';

  const activeItemQuantity = activeTicket?.items?.find((i) => i.ID === item.ID)?.quantity || 0;

  // Determine the display price based on priority:
  // 1. customerTierPriceAfterTax (if exists and customer is selected)
  // 2. priceAfterTax (if exists)
  // 3. price (fallback)
  const displayPrice = item.customerTierPriceAfterTax ?? item.priceAfterTax ?? item.price;

  // Determine sizes based on the size prop
  const iconSizes = {
    small: 'w-[28px] h-[28px]',
    medium: 'w-[37px] h-[37px]',
    large: 'w-[48px] h-[48px]',
  };

  const textSizes = {
    small: 'text-[12px]',
    medium: 'text-[14px]',
    large: 'text-[16px]',
  };

  const priceSizes = {
    small: 'text-[11px]',
    medium: 'text-[13px]',
    large: 'text-[15px]',
  };

  const stockSizes = {
    small: 'text-[10px]',
    medium: 'text-[12px]',
    large: 'text-[14px]',
  };

  const containerPadding = {
    small: 'p-1.5',
    medium: 'p-2.5',
    large: 'p-3',
  };

  return (
    <div
      onClick={() => onItemPress(item)}
      className={`relative flex flex-col items-center bg-white hover:bg-gray-50 gap-1.5 ${containerPadding[size]} 
        border border-gray-200 shadow-sm rounded-lg dark:border-gray-700 cursor-pointer 
        transition-all duration-200 hover:shadow-md ${className} ${!item.enabled ? 'opacity-60' : ''}`}
    >
      {activeItemQuantity > 0 && (
        <div
          className={`absolute ${size === 'small' ? 'top-0.5 right-0.5' : 'top-1 right-1'} 
            ${size === 'small' ? 'min-w-[18px] h-[18px] text-xs' : 'min-w-[22px] h-[22px] text-sm'}
            flex items-center justify-center bg-blue-500 text-white font-semibold rounded-full px-1`}
        >
          {activeItemQuantity}
        </div>
      )}
      {item.iconType === 'COLOR' && (
        <div
          className={`${iconSizes[size]} rounded-full`}
          style={{ backgroundColor: item.iconValue }}
        />
      )}
      {item.iconType === 'IMAGE' && (
        <div className={`${iconSizes[size]} rounded-full overflow-hidden`}>
          <img src={item.iconValue} alt={item.name} className="w-full h-full object-cover" />
        </div>
      )}
      <div className="flex flex-col gap-1 items-center justify-center w-full">
        <span
          className={`${textSizes[size]} font-semibold text-gray-800 dark:text-gray-200 text-center truncate w-full`}
        >
          {isArabic && item.nameAr ? item.nameAr : item.name}
        </span>
        {displayPrice && (
          <span className={`${priceSizes[size]} font-semibold text-gray-600 dark:text-gray-300`}>
            {displayPrice.toFixed(2)} {currency}
          </span>
        )}
        {item.stock !== null && (
          <span className={`${stockSizes[size]} text-gray-500 dark:text-gray-400`}>
            {item.stock} {t('common:in_stock')}
          </span>
        )}
      </div>
    </div>
  );
};

export default ItemCard;
